import { IException, ICopyExceptionAction, IPreventExceptionAction, IMessageExceptionAction, IMetalGroup } from "../../sharedInterfaces";
import { message } from "antd";
import { RingStateController } from "./RingStateController";


export class ExceptionHandler {
  private _ringStateController:RingStateController;

  constructor(ringStateController:RingStateController) {
    this._ringStateController = ringStateController;
    this._initialize();
  };

  //Mostly just interface stuff for messages. If anything other than exceptions ever uses messages, this should probably be moved elsewhere
  private _initialize = () => {
    message.config({
      duration: 3,
      getContainer: () => document.querySelector('.threed-configurator-container')
    });

    const container = document.querySelector('.threed-configurator-container');

    const removeMessageRecursive = (element:HTMLElement) => {
      if (element.classList && element.classList.contains('ant-message-notice')) {
        element.remove();
      } else if (element.parentElement) {
        removeMessageRecursive(element.parentElement);
      }
    }

    container.addEventListener('click', (e:MouseEvent) => {
      let element = e.target as HTMLElement;
      removeMessageRecursive(element);
    })
  }// /();

  public handleException = (exception:IException, changedMetalGroupName:string, ignoreExceptionActions:string[] = []) => {
    //console.log(exception);
    let conditionsTrue = this._checkConditions(exception, changedMetalGroupName)
    if (conditionsTrue) this._executeActions(exception, ignoreExceptionActions);
  }// /()

  //Checks an exception's conditions. Returns true if all are met, otherwise returns false.
  private _checkConditions = (exception:IException, changedMetalGroupName:string):boolean => {
    const metalGroups = this._ringStateController.getState().metalGroups;
    let allTrue = true;

    exception.conditions.forEach((condition) => {
      try {
        const metalGroup = metalGroups.find(rmg => rmg.name === condition.group);
        if (exception.type === 'material' && !condition.option.includes(metalGroup.material)) {
          allTrue = false;
        }
        if (exception.type === 'surface' && !condition.option.includes(metalGroup.surface)) {
          allTrue = false;
        }
        if (condition.wasChanged && !(changedMetalGroupName === condition.group)) {
          allTrue = false;
        }
      } catch(err) {
        console.warn("Error checking exception condition!", condition, exception, err);
      };
    });
    return allTrue;
  }// /()

  //
  private _executeActions = (exception:IException, ignoreExceptionActions:string[]) => {
    exception.actions.forEach((act) => {
      try {
        switch (act.type) {
          case 'copy':
            if (ignoreExceptionActions.includes('copy')) break;
            this._executeCopyAction(act as ICopyExceptionAction, exception.type);
            break;
          case 'prevent':
            if (ignoreExceptionActions.includes('prevent')) break;
            this._executePreventAction(act as IPreventExceptionAction, exception.type);
            break;
          case 'message':
            if (ignoreExceptionActions.includes('message')) break;
            this._executeMessageAction(act as IMessageExceptionAction);
            break;
        };
      } catch(err) {
        console.error("Error executing exception action!", act, exception, err);
      };
    });
  }// /()

  private _executeCopyAction = (action:ICopyExceptionAction, exceptionType:'material'|'surface') => {
    const metalGroups = this._ringStateController.getState().metalGroups;
    const sourceGroup = metalGroups.find(rmg => rmg.name === action.from);

    action.to.forEach((targetGroupName) => {
      const targetGroup = metalGroups.find(rmg => rmg.name === targetGroupName);
      let updatedTargetGroup:IMetalGroup;

      if (exceptionType === 'material') {
        updatedTargetGroup = Object.assign({}, targetGroup, { material: sourceGroup.material, purity: sourceGroup.purity });
      } else if (exceptionType === 'surface') {
        updatedTargetGroup = Object.assign({}, targetGroup, { surface: sourceGroup.surface, purity: sourceGroup.purity });
      }

      this._ringStateController.updateMetalGroup(updatedTargetGroup, { ignoreExceptionActions: ['copy', 'prevent', 'message'] });
    });
  }// /()

  private _executePreventAction = (action:IPreventExceptionAction, exceptionType:'material'|'surface') => {
    const metalGroups = this._ringStateController.getState().metalGroups;

    action.group.forEach((targetGroupName) => {
      const targetGroup = metalGroups.find(rmg => rmg.name === targetGroupName);
      action.option.forEach((option) => {
        if (exceptionType === 'material') {
          if (!targetGroup.disabledMaterialOptions.includes(option)) {
            targetGroup.disabledMaterialOptions.push(option);
          }
        } else if (exceptionType === 'surface') {
          if (!targetGroup.disabledSurfaceOptions.includes(option)) {
            targetGroup.disabledSurfaceOptions.push(option);
          }
        }
      })
    })
  }// /()

  private _executeMessageAction = (action:IMessageExceptionAction) => {
    message.info({content: action.message});
  }// /()

}// /class